// --------------------------------------------------------------
// Created On: 2025-10-22
// Author: Marco Serrano
//
// Last Modified: 2026-07-31
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import AssetManagementWizard from "../AssetManagementWizard/AssetManagementWizard";
import { GetDeviceMapResponseBody } from "src/types/api/GetDeviceMapResponseBody";
import useReduxSelector from "../../hooks/useReduxSelector";
import apiRequest from "../../utilities/api/apiRequest";
import { API } from "../../constants/miscellaneous";
import Spinner from "../Spinner/Spinner";

// Button for editing an asset and getting any data needed to use the AssetManagementWizard
export default function AssetEditButton(props: Props): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const initialDeviceMap: GetDeviceMapResponseBody = {
    controllers: [],
    deviceTypes: [],
    productManufacturers: [],
    productTypes: [],
    monitoringDevices: [],
    analogSensors: [],
    digitalSensors: [],
    digitalOutputs: [],
    templates: [],
    assetMapIcons: [],
    modbusSensors: [],
    gatewayAssets: [],
    runtimeServiceAttributeMap: {},
  };

  const [deviceMap, setDeviceMap] = useState<GetDeviceMapResponseBody>(initialDeviceMap);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  // Calls endpoint to retrieve the asset management settings that will populate the wizard.
  async function getAssetManagementSettings(): Promise<void> {
    setLoading(true);
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/device`,
      "GET",
      null
    )) as [Response, GetDeviceMapResponseBody];

    if (response.ok && responseBody) {
      setDeviceMap(responseBody);
    }
    setLoading(false);
  }

  async function handleEditAsset(): Promise<void> {
    await getAssetManagementSettings();
    setLoading(false);
    setShowModal(true);
  }

  // Closes the AssetManagementWizard modal and then does any additional behavior the parent component defines
  function onClose(): void {
    setShowModal(false);
    props.onClose();
  }

  return (
    <div>
      <button className="btn btn-light" type="button" onClick={handleEditAsset}>
        Edit Asset
      </button>
      <Spinner loading={loading} />
      <AssetManagementWizard
        assetId={props.assetId}
        showModal={showModal}
        isCreatingNewAsset={false}
        disableDataCollectionSelection={false}
        templates={deviceMap.templates}
        assetMapIcons={deviceMap.assetMapIcons}
        deviceTypes={deviceMap.deviceTypes}
        monitoringDevices={deviceMap.monitoringDevices}
        gatewayAssets={deviceMap.gatewayAssets}
        productTypes={deviceMap.productTypes}
        productManufacturers={deviceMap.productManufacturers}
        controllers={deviceMap.controllers}
        modbusSensors={deviceMap.modbusSensors}
        analogSensors={deviceMap.analogSensors}
        digitalSensors={deviceMap.digitalSensors}
        runtimeServiceAttributeMap={deviceMap.runtimeServiceAttributeMap}
        onClose={onClose}
        onAction={() => {
          /* Do nothing. */
        }}
        hideDelete={true}
      />
    </div>
  );
}

interface Props {
  assetId: number;
  onClose: () => void;
}
